/**
 * CDN resolver для медиа-путей из GraphQL API.
 *
 * Контракт: GraphQL возвращает пути БЕЗ домена (например, '/users/123/avatar.png'
 * или 'media/abc.jpg'). На клиенте их надо скармливать <img>/<video>/<source>,
 * для чего нужен полный URL с CDN-доменом.
 *
 * Источник CDN-домена (как в iOS KOService.swift + AppSettingsStorageService):
 *   1. GetAppConfigQuery.appConfig.cdns[{ key, url }] — динамически от backend
 *   2. Fallback: https://iw4ols40zk.cdn.twcstorage.ru (как в AGENTS.md)
 *
 * Эта реализация:
 *   - По умолчанию использует fallback
 *   - setCdnBase(url) позволяет переопределить (например, из appConfig)
 *   - mediaUrl(path) — главная точка входа, используется в <img>/<video>/<source>
 */

const DEFAULT_CDN = 'https://iw4ols40zk.cdn.twcstorage.ru';

let cdnBase: string = DEFAULT_CDN;

/** Переопределить базу CDN (без trailing slash). */
export function setCdnBase(url: string | null | undefined): void {
  if (!url) return;
  cdnBase = url.replace(/\/+$/, '');
}

/** Текущая база CDN (с протоколом, без trailing slash). */
export function getCdnBase(): string {
  return cdnBase;
}

/** Сбросить к fallback (нужно для тестов). */
export function resetCdnBase(): void {
  cdnBase = DEFAULT_CDN;
}

/**
 * Превратить относительный media-path в полный URL.
 *
 * Правила:
 *   - null/undefined/пусто → undefined (чтобы React не рендерил <img src="undefined">)
 *   - Уже абсолютный (http(s)://, //, data:, blob:) → вернуть как есть
 *   - Относительный → prepend CDN base с правильным слэшем
 */
export function mediaUrl(path?: string | null): string | undefined {
  if (path === null || path === undefined || path === '') return undefined;

  const trimmed = String(path).trim();
  if (!trimmed) return undefined;

  // Уже абсолютный URL с протоколом
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Protocol-relative: //domain/path
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  // data: / blob: URI
  if (/^(data|blob):/i.test(trimmed)) return trimmed;

  // Относительный путь — добавляем CDN base
  const slash = trimmed.startsWith('/') ? '' : '/';
  return `${cdnBase}${slash}${trimmed}`;
}

/**
 * Извлечь URL CDN из GetAppConfig ответа.
 * В схеме AppConfig.cdns — массив { key, url }. iOS ищет конкретный key;
 * мы для простоты берём первый попавшийся (обычно он один).
 *
 * @returns baseUrl | null если в config нет CDN-записей
 */
export function extractCdnBaseFromAppConfig(
  appConfig: unknown
): string | null {
  if (!appConfig || typeof appConfig !== 'object') return null;
  const cfg = appConfig as { cdns?: Array<{ key?: string | null; url?: string | null }> };
  const cdns = cfg.cdns;
  if (!Array.isArray(cdns) || cdns.length === 0) return null;

  // Предпочтение по ключу (в порядке приоритета — как в iOS):
  const preferredKeys = ['media', 'main', 'default', 'cdn'];
  for (const key of preferredKeys) {
    const found = cdns.find((c) => c?.key === key && c?.url);
    if (found?.url) return found.url;
  }
  // Иначе первый с url
  const any = cdns.find((c) => c?.url);
  return any?.url ?? null;
}
